import * as circularProgressModes from "../../enums/circularProgressModes";
import * as colors from "../../styles/colors";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { StyleSheet, css } from "aphrodite";

function createTransition(duration, property, delay, easeFunction) {
	duration = duration || "450ms";
	property = property || "all";
	delay = delay || "0ms";
	easeFunction = easeFunction || "linear";

	return `${property} ${duration} ${easeFunction} ${delay}`;
}

const styles = StyleSheet.create({
	svg: {
		position: "relative",
	},
});

export default class CircularProgress extends PureComponent {
	static propTypes = {
		className: PropTypes.string,
		color: PropTypes.string,
		innerStyle: PropTypes.object,
		max: PropTypes.number,
		min: PropTypes.number,
		mode: PropTypes.oneOf([
			circularProgressModes.DETERMINATE,
			circularProgressModes.INDETERMINATE,
		]),
		size: PropTypes.number,
		style: PropTypes.object,
		thickness: PropTypes.number,
		value: PropTypes.number,
	};

	static defaultProps = {
		mode: circularProgressModes.INDETERMINATE,
		value: 0,
		min: 0,
		max: 100,
		size: 40,
		thickness: 3.5,
	};

	componentDidMount() {
		this.scalePath(this.path);
		this.rotateWrapper(this.wrapper);
	}

	componentWillUnmount() {
		clearTimeout(this.scalePathTimer);
		clearTimeout(this.rotateWrapperTimer);
	}

	scalePath = (path, step) => {
		step = step ? step : 0;

		if (!path || this.props.mode !== circularProgressModes.INDETERMINATE) {
			return;
		}

		step %= 3;

		if (step === 0) {
			path.style.strokeDasharray = this.getArcLength(0) + ", " + this.getArcLength(1);
			path.style.strokeDashoffset = 0;
			path.style.transitionDuration = "0ms";
		} else if (step === 1) {
			path.style.strokeDasharray = this.getArcLength(0.7) + ", " + this.getArcLength(1);
			path.style.strokeDashoffset = this.getArcLength(-0.3);
			path.style.transitionDuration = "750ms";
		} else {
			path.style.strokeDasharray = this.getArcLength(0.7) + ", " + this.getArcLength(1);
			path.style.strokeDashoffset = this.getArcLength(-1);
			path.style.transitionDuration = "850ms";
		}
		this.scalePathTimer = setTimeout(
			() => {
				return this.scalePath(path, step + 1);
			},
			step ? 750 : 250,
		);
	};

	rotateWrapper = wrapper => {
		if (!wrapper || this.props.mode !== circularProgressModes.INDETERMINATE) return;
		wrapper.style.transform = "rotate(0deg)";
		wrapper.style.transitionDuration = "0ms";

		setTimeout(function() {
			wrapper.style.transform = "rotate(1800deg)";
			wrapper.style.transitionDuration = "10s";
			wrapper.style.transitionTimingFunction = "linear";
		}, 50);
		this.rotateWrapperTimer = setTimeout(() => {
			return this.rotateWrapper(wrapper);
		}, 10050);
	};

	getRelativeValue = (value, min, max) => {
		const clampedValue = Math.min(Math.max(min, value), max);
		return clampedValue / (max - min);
	};

	getArcLength = fraction => {
		return fraction * Math.PI * (this.props.size - this.props.thickness);
	};

	getStyles = () => {
		const max = this.props.max,
			min = this.props.min,
			size = this.props.size,
			value = this.props.value;

		const inlineStyles = {
			root: {
				position: "relative",
				display: "inline-block",
				width: size,
				height: size,
			},
			wrapper: {
				width: size,
				height: size,
				display: "inline-block",
				transition: createTransition("transform", "20s", null, "linear"),
				transitionTimingFunction: "linear",
			},
			svg: {
				width: size,
				height: size,
				position: "relative",
			},
			path: {
				stroke: this.props.color || colors.docketLightishBlue,
				strokeLinecap: "round",
				transition: createTransition("all", "1.5s", null, "ease-in-out"),
			},
		};

		if (this.props.mode === circularProgressModes.DETERMINATE) {
			const relVal = this.getRelativeValue(value, min, max);
			inlineStyles.path.transition = createTransition("all", "0.3s", null, "linear");
			inlineStyles.path.strokeDasharray =
				this.getArcLength(relVal) + ", " + this.getArcLength(1);
		}

		return inlineStyles;
	};

	_setWrapperRef = ref => {
		this.wrapper = ref;
	};

	_setPathRef = ref => {
		this.path = ref;
	};

	render() {
		const style = this.props.style;
		const innerStyle = this.props.innerStyle;
		const size = this.props.size;
		const thickness = this.props.thickness;

		const inlineStyles = this.getStyles();

		return (
			<div className={this.props.className} style={{ ...inlineStyles.root, ...style }}>
				<div ref={this._setWrapperRef} style={{ ...inlineStyles.wrapper, ...innerStyle }}>
					<svg className={css(styles.svg)} viewBox={`0 0 ${size} ${size}`}>
						<circle
							ref={this._setPathRef}
							style={inlineStyles.path}
							cx={size / 2}
							cy={size / 2}
							r={(size - thickness) / 2}
							fill="none"
							strokeWidth={thickness}
							strokeMiterlimit={20}
						/>
					</svg>
				</div>
			</div>
		);
	}
}
